// Web helper for gathering current user context to attach to errorEvents.
// Readable outside React (useful in global handlers + Sentry hooks).
//
// isDesignPartner is populated by WebActivityTracker (which already reads the
// user doc) via setIsDesignPartner(uid, bool). Auto-context tries to read it;
// if unset, the field is omitted from the error event.

import { useAuthStore } from "@eventify/shared/state/authStore";
import { PROD_HOSTNAMES } from "@eventify/shared/config/hostnames";

export interface CurrentWebContext {
  userId?: string;
  userRole?: string;
  isDesignPartner?: boolean;
  screen?: string;
  buildVersion?: string;
}

const _isDesignPartner = new Map<string, boolean>();

export function setIsDesignPartner(uid: string, value: boolean): void {
  _isDesignPartner.set(uid, value);
}

export function getIsDesignPartner(uid: string | undefined): boolean | undefined {
  if (!uid) return undefined;
  return _isDesignPartner.get(uid);
}

/**
 * Hostname-based environment classification.
 *
 * NODE_ENV is unreliable for this — Next.js sets NODE_ENV=production for EVERY
 * built bundle (both dev and prod hosting targets), so anything from
 * therunup-web.web.app would incorrectly trigger Claude triage + Telegram
 * alerts. The actual signal is which hostname we're serving from.
 *
 * Three buckets, but only two values to keep the trigger logic simple:
 *   - "development" → localhost OR dev hosting (therunup-web.*, dev Cloud Run)
 *   - "production"  → prod hosting only (therunup.ai, therunup-prod.*)
 *
 * SSR fallback returns "development" — if we render an event during SSR before
 * the browser hostname is known, we err on the side of NOT pinging Telegram.
 */
export function detectEnvironment(): "development" | "production" {
  if (typeof window === "undefined") return "development";
  try {
    const host = (window.location?.hostname ?? "").toLowerCase();
    // PROD_HOSTNAMES covers the 4 canonical prod domains. The two startsWith
    // checks cover platform-versioned URLs:
    //   - "therunup-ssr-prod-" matches Cloud Run revisions like
    //     therunup-ssr-prod-00084-dct-uc.a.run.app  (trailing dash required —
    //     prevents a hypothetical hostile "therunup-ssr-prod.attacker.com"
    //     from being classified production).
    //   - "therunup-prod--" matches Firebase Hosting preview URLs which use
    //     a DOUBLE-dash separator: therunup-prod--<channel>-<hash>.web.app
    //     (single-dash forms like "therunup-prod-evil.web.app" are NOT
    //     allocatable to a different Firebase site under our project; the
    //     double-dash is Firebase's preview-channel marker).
    // Source of truth for the canonical hostname set:
    // packages/shared/config/hostnames.ts.
    if (
      PROD_HOSTNAMES.has(host) ||
      host.startsWith("therunup-ssr-prod-") ||
      host.startsWith("therunup-prod--")
    ) {
      return "production";
    }
    // localhost, 127.0.0.1, therunup-web.*, therunup-ssr-dev-*, anything else
    return "development";
  } catch {
    return "development";
  }
}

export function getCurrentWebContext(): CurrentWebContext {
  const ctx: CurrentWebContext = {};
  try {
    const user = useAuthStore.getState().user;
    if (user?.id) ctx.userId = user.id;
    if (user?.role) ctx.userRole = user.role;
    const dp = getIsDesignPartner(user?.id);
    if (typeof dp === "boolean") ctx.isDesignPartner = dp;
  } catch {
    // store not ready
  }
  try {
    if (typeof window !== "undefined" && window.location?.pathname) {
      ctx.screen = window.location.pathname;
    }
  } catch {
    // ignore
  }
  try {
    const sha = (process.env.NEXT_PUBLIC_BUILD_SHA || "").slice(0, 12);
    if (sha) ctx.buildVersion = sha;
  } catch {
    // ignore
  }
  return ctx;
}
